import { useState, useEffect } from 'react';
import {
  Search, Award, ChevronLeft, ChevronRight,
  AlertTriangle, ExternalLink, CheckCircle, XCircle, Clock, RotateCcw,
} from 'lucide-react';

interface CredentialReviewItem {
  user_id: string;
  user_email: string;
  user_name: string | null;
  cert_id: string;
  cert_name: string;
  issuer: string;
  date: string;
  credential_url: string;
  credential_ext: string | null;
  review_status: 'PENDING' | 'APPROVED' | 'REJECTED';
  credential_uploaded_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
}

type Filter = 'all' | 'pending' | 'approved' | 'rejected';

export default function AdminCertificateReviewTab() {
  const [items, setItems] = useState<CredentialReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('pending');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [rejectModal, setRejectModal] = useState<CredentialReviewItem | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        filter,
        ...(activeSearch && { search: activeSearch }),
      });
      const response = await fetch(`/api/v1/admin/certificate-credentials?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('자격증 검토 목록을 불러오지 못했습니다.');
      }
      const data = await response.json();
      setItems(data.items || []);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeSearch, filter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchTerm);
  };

  const handleShowAll = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
    setFilter('all');
  };

  const updateStatus = async (
    item: CredentialReviewItem,
    status: 'APPROVED' | 'REJECTED' | 'PENDING',
    rejectionReason?: string,
  ) => {
    const key = `${item.user_id}/${item.cert_id}`;
    setUpdatingKey(key);
    setMessage(null);
    try {
      const response = await fetch(
        `/api/v1/admin/certificate-credentials/${item.user_id}/${item.cert_id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, rejectionReason }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || '검토 상태 변경에 실패했습니다.');
      }
      setMessage({ type: 'success', text: data.message || '검토 상태가 업데이트되었습니다.' });
      await fetchItems();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || '오류가 발생했습니다.' });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleApprove = (item: CredentialReviewItem) => updateStatus(item, 'APPROVED');
  const handleReject = (item: CredentialReviewItem) => {
    setRejectModal(item);
    setRejectReason(item.rejection_reason || '');
  };
  const confirmReject = () => {
    if (!rejectModal) return;
    updateStatus(rejectModal, 'REJECTED', rejectReason.trim() || undefined);
    setRejectModal(null);
    setRejectReason('');
  };
  const handleResetToPending = (item: CredentialReviewItem) => updateStatus(item, 'PENDING');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  const renderStatusBadge = (status: string) => {
    if (status === 'APPROVED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
          <CheckCircle className="w-3 h-3" /> 인증
        </span>
      );
    }
    if (status === 'REJECTED') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 border border-red-200">
          <XCircle className="w-3 h-3" /> 반려
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200">
        <Clock className="w-3 h-3" /> 대기
      </span>
    );
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Award className="w-6 h-6 text-accent" />
          자격증·수료증 검토
        </h1>
        <p className="text-gray-500 mt-1">
          전체 {totalCount}건 — 회원이 업로드한 자격증/수료증 증명 파일을 검토하고 인증하세요.
        </p>
      </div>

      {message && (
        <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {/* 필터 + 검색 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {(['pending', 'approved', 'rejected', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f === 'all' ? '전체' : f === 'pending' ? '검토 대기' : f === 'approved' ? '인증 완료' : '반려'}
            </button>
          ))}
        </div>

        <form onSubmit={handleSearch} className="flex-1 min-w-[200px] flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="이메일·이름·자격증명·발급기관 검색"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
            검색
          </button>
          {(activeSearch || filter !== 'all') && (
            <button type="button" onClick={handleShowAll} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
              전체 보기
            </button>
          )}
        </form>
      </div>

      {/* 본문 */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">불러오는 중...</div>
      ) : error ? (
        <div className="bg-red-50 text-red-700 rounded-xl border border-red-200 p-6">{error}</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
          조건에 맞는 자격증 증명 파일이 없습니다.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">제출자</th>
                  <th className="px-4 py-3 text-left font-medium">자격증</th>
                  <th className="px-4 py-3 text-left font-medium">증명 파일</th>
                  <th className="px-4 py-3 text-left font-medium">제출일</th>
                  <th className="px-4 py-3 text-center font-medium">상태</th>
                  <th className="px-4 py-3 text-center font-medium">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const key = `${item.user_id}/${item.cert_id}`;
                  const isUpdating = updatingKey === key;
                  return (
                    <tr key={key} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.user_name || '-'}</div>
                        <div className="text-xs text-gray-500">{item.user_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.cert_name || '-'}</div>
                        <div className="text-xs text-gray-500">
                          {item.issuer && `${item.issuer} · `}
                          {item.date}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <a href={item.credential_url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-center gap-1 text-accent hover:underline">
                          파일 열기
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        {item.credential_ext && (
                          <div className="text-xs text-gray-400 mt-0.5 uppercase">{item.credential_ext}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(item.credential_uploaded_at)}</td>
                      <td className="px-4 py-3 text-center">
                        {renderStatusBadge(item.review_status)}
                        {item.review_status === 'REJECTED' && item.rejection_reason && (
                          <div className="text-xs text-red-500 mt-1 max-w-[160px] mx-auto">
                            {item.rejection_reason}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {item.review_status !== 'APPROVED' && (
                            <button
                              onClick={() => handleApprove(item)}
                              disabled={isUpdating}
                              className="p-1.5 text-green-600 border border-green-200 rounded-lg hover:bg-green-50 disabled:opacity-50"
                              title="인증"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          {item.review_status !== 'REJECTED' && (
                            <button
                              onClick={() => handleReject(item)}
                              disabled={isUpdating}
                              className="p-1.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                              title="반려"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          {item.review_status !== 'PENDING' && (
                            <button
                              onClick={() => handleResetToPending(item)}
                              disabled={isUpdating}
                              className="p-1.5 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                              title="대기 상태로 되돌리기"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <span className="text-sm text-gray-500">{page} / {totalPages} 페이지 (총 {totalCount}건)</span>
              <div className="flex gap-1">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                        className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="p-1.5 rounded-md border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-100">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 반려 모달 */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setRejectModal(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900">자격증 인증을 반려하시겠습니까?</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              <strong>{rejectModal.user_name || rejectModal.user_email}</strong>의 <strong>{rejectModal.cert_name}</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 mt-4">반려 사유 (선택)</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="예: 파일이 흐릿하여 내용을 확인할 수 없습니다."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">반려 사유는 사용자에게 알림으로 전달됩니다.</p>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setRejectModal(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={confirmReject}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
              >
                반려
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
